// BestMomentsCreationConfig component
import { CaptionsStyleSelector } from '../creation/CaptionsStyleSelector';
import posthog from 'posthog-js';
import { useTranslation } from 'react-i18next';

export interface BestMomentsConfigRequestData {
  captions_enabled: boolean;
  caption_config: {
    color?: string;
    accent_color?: string;
    font_size?: number;
    accent_font_size?: number;
    words_per_line?: number;
    vertical_margin?: number;
    style_id?: string;
  };
}

interface BestMomentsCreationConfigProps {
  config: BestMomentsConfigRequestData;
  onConfigChange: (config: BestMomentsConfigRequestData) => void;
}

/**
 * BestMomentsCreationConfig component
 * 
 * This component handles the configuration UI for the best moments strategy
 * during the creation flow. It allows users to modify settings before processing.
 */
export function BestMomentsCreationConfig({ config, onConfigChange }: BestMomentsCreationConfigProps) {
  const { t } = useTranslation();

  const toggleCaptions = () => {
    onConfigChange({
      ...config,
      captions_enabled: !config.captions_enabled
    });
  };

  const handleColorChange = (type: 'color' | 'accent_color', value: string | undefined) => {
    onConfigChange({
      ...config,
      caption_config: {
        ...config.caption_config,
        [type]: value,
        // If colors are being customized, reset font sizes unless they have their own UI
        font_size: undefined,
        accent_font_size: undefined,
      }
    });
  };

  const handleFontSizeChange = (type: 'font_size' | 'accent_font_size', value: number | undefined) => {
    onConfigChange({
      ...config,
      caption_config: {
        ...config.caption_config,
        [type]: value,
      }
    });
  };

  const handleResetCustomizations = () => {
    onConfigChange({
      ...config,
      caption_config: {
        // Preserve style_id and other non-customizable fields
        style_id: config.caption_config.style_id,
        words_per_line: config.caption_config.words_per_line, // Assuming this is not part of color/font customization UI here
        vertical_margin: config.caption_config.vertical_margin, // Assuming this is not part of color/font customization UI here
        // Reset color and font related properties
        color: undefined,
        accent_color: undefined,
        font_size: undefined,
        accent_font_size: undefined,
      }
    });
  };

  const handleStyleChange = (styleId: string) => {
    onConfigChange({
      ...config,
      caption_config: {
        ...config.caption_config,
        style_id: styleId
      }
    });
    posthog.capture('custom_caption_chosen', { strategy_type: 'best_moments', style_id: styleId });
  };

  return (
    <div className="md:grid md:grid-cols-1 md:gap-6 w-full">
      {/* Captions Section */}
      <div className="mb-6 md:mb-0 p-4 lg:p-6 rounded-2xl bg-surface-interactive dark:bg-surface-interactive-dark shadow-interactive dark:border dark:border-interactive-dark flex flex-col h-full md:min-h-[500px]">
        <h2 className="text-lg font-medium mb-3 text-content-strong">{t('kovrificationCreationConfig.captionsTitle')}</h2>
        
        <div className="flex items-center mb-5">
          <div className="w-5 h-5 mr-2 relative">
            <input
              type="checkbox"
              id="enable-captions"
              checked={config.captions_enabled}
              onChange={toggleCaptions}
              className="sr-only peer"
            />
            <label 
              htmlFor="enable-captions" 
              className="flex items-center justify-center w-5 h-5 rounded peer-checked:bg-primary bg-input dark:bg-input-dark border border-input dark:border-input-dark peer-checked:border-primary cursor-pointer"
            >
              {config.captions_enabled && (
                <span className="material-symbols-outlined text-on-primary text-xs">check</span>
              )}
            </label>
          </div>
          <label htmlFor="enable-captions" className="text-sm text-body cursor-pointer">{t('kovrificationCreationConfig.enableCaptionsLabel')}</label>
        </div>

        {config.captions_enabled ? (
          <div className="flex-grow flex flex-col">
            <CaptionsStyleSelector
              selected={config.caption_config.style_id || 'karaoke_default_v1'}
              onSelectStyle={handleStyleChange}
              color={config.caption_config.color}
              accentColor={config.caption_config.accent_color}
              fontSize={config.caption_config.font_size}
              accentFontSize={config.caption_config.accent_font_size}
              onColorChange={handleColorChange}
              onFontSizeChange={handleFontSizeChange}
              onResetCustomizations={handleResetCustomizations}
            />
          </div>
        ) : (
          <div className="flex-grow flex items-center justify-center">
            <div className="text-center text-muted">
              <span className="material-symbols-outlined text-4xl mb-2">closed_caption_off</span>
              <p>{t('kovrificationCreationConfig.captionsDisabledPlaceholder')}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
} 